import {
  DEFAULT_LEVEL,
  LEVELS,
  LEVEL_MIN_ATTEMPTS,
  LEVEL_PROMOTE_THRESHOLD,
} from "../config/defaults";
import type { Card, CefrLevel, LevelCounts, LevelEstimate, StatsMap } from "../types";

/**
 * Compute success rate per CEFR level and estimate the user's level.
 * Ascends the CEFR ladder contiguously: a level promotes the estimate
 * only if attempts ≥ minAttempts AND success rate ≥ promoteThreshold,
 * and the ascent stops at the first level where that fails. Levels
 * with zero cards in the deck are skipped transparently (can't be
 * evaluated), so a bilingual deck that only carries B1+ cards still
 * works with a floor below. `floor` raises the minimum — the
 * estimator may still PROMOTE above it, but never below — and also
 * marks the starting point (lower levels are assumed passed).
 *
 * Guard against the old skip-to-top behavior that could promote a user
 * from A2 straight to C2 if they happened to accumulate 15 correct C2
 * attempts, even though they'd never cleared B1/B2/C1.
 */
export function estimateUserLevel(
  cards: Card[],
  stats: StatsMap,
  floor: CefrLevel | null = null,
): LevelEstimate {
  const counts = {} as LevelCounts;
  const cardsPerLevel = {} as Record<CefrLevel, number>;
  for (const lv of LEVELS) {
    counts[lv] = { attempts: 0, correct: 0, rate: 0 };
    cardsPerLevel[lv] = 0;
  }
  for (const c of cards) {
    const lv = (c.level || DEFAULT_LEVEL) as CefrLevel;
    if (cardsPerLevel[lv] != null) cardsPerLevel[lv] += 1;
    const s = stats[c.id];
    if (!s || !s.attempts) continue;
    if (counts[lv]) {
      counts[lv].attempts += s.attempts;
      counts[lv].correct += s.correct || 0;
    }
  }
  for (const lv of LEVELS) {
    const cc = counts[lv];
    cc.rate = cc.attempts ? cc.correct / cc.attempts : 0;
  }
  const startIdx = floor ? LEVELS.indexOf(floor) : 0;
  let est: CefrLevel = floor ?? DEFAULT_LEVEL;
  for (let i = startIdx; i < LEVELS.length; i++) {
    const lv = LEVELS[i]!;
    // Levels with no cards in this deck can't be evaluated — skip them
    // rather than blocking the ascent (e.g. a B1+-only deck).
    if (cardsPerLevel[lv] === 0) continue;
    const cc = counts[lv];
    if (cc.attempts >= LEVEL_MIN_ATTEMPTS && cc.rate >= LEVEL_PROMOTE_THRESHOLD) {
      est = lv;
    } else {
      // Either not enough samples or below threshold: don't promote
      // through a level we haven't cleared.
      break;
    }
  }
  return { level: est, counts };
}

// ---------- Mono-deck mastery ladder (star tiers) ----------
// CEFR doesn't apply to mono decks (no card-level difficulty labels),
// so we derive a parallel 5-tier progression from the fraction of
// cards in Leitner box 5 ("mastered"). The UI shows 5 visible stars
// with the filled count matching the current tier — same intent as
// the CEFR pill, universal across any mono subject (geography today,
// plants / medical / … tomorrow).

/** Thresholds are %-of-mastered lower bounds. tier N requires pct ≥
 *  MASTERY_TIER_THRESHOLDS[N-1]. 5 tiers at 0 / 20 / 40 / 60 / 80. */
export const MASTERY_TIER_THRESHOLDS = [0, 20, 40, 60, 80] as const;
export const MASTERY_MAX_TIER = 5;

/** 1..5 based on the fraction of cards in box 5. Empty decks default
 *  to tier 1 so the pill never renders blank. */
export function computeMasteryTier(
  masteredCount: number,
  totalCards: number,
): number {
  if (totalCards <= 0) return 1;
  const pct = (masteredCount / totalCards) * 100;
  let tier = 1;
  for (let i = 0; i < MASTERY_TIER_THRESHOLDS.length; i++) {
    if (pct >= MASTERY_TIER_THRESHOLDS[i]!) tier = i + 1;
  }
  return tier;
}

/** 5-star glyph string with the current tier filled — e.g. tier 3
 *  returns "★★★☆☆". Unicode U+2605 / U+2606; renders crisply in the
 *  mono font used by the level pill. */
export function starsForTier(tier: number): string {
  const n = Math.max(1, Math.min(MASTERY_MAX_TIER, tier | 0));
  return "★".repeat(n) + "☆".repeat(MASTERY_MAX_TIER - n);
}

/** How many more mastered cards the user needs to cross into the
 *  NEXT tier. Returns null at max tier (already at ★★★★★). */
export function cardsToNextTier(
  masteredCount: number,
  totalCards: number,
): number | null {
  const tier = computeMasteryTier(masteredCount, totalCards);
  if (tier >= MASTERY_MAX_TIER) return null;
  const nextPct = MASTERY_TIER_THRESHOLDS[tier]!; // next tier's floor
  // ceil so partial cards round UP — "you need 5 more" not "you need
  // 4.7 more", which would suggest you're already across the line.
  const needed = Math.ceil((nextPct / 100) * totalCards);
  return Math.max(0, needed - masteredCount);
}
